import { array, nonEmptyArray, record } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { get, isEmpty } from 'lodash/fp';
import { createSelector } from 'reselect';

import { TaxValue } from '@mews-distributor/common';
import { PricingMode } from '@mews/enums';
import { getStringProp } from '@mews/fp';
import { subtractNumbersAsDecimals, sumNumbersAsDecimals } from '@mews/framework';

import { getCurrencyCode, getLanguageCode } from '../../../../modules/app/selectors';
import {
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
    getReservationsTotalPrice,
} from '../../../../modules/entities/reservations/selectors';
import { getTaxRates, localizeCurrencyValue } from '../../../../modules/globalization/selectors';
import { TaxRate } from '../../../../modules/globalization/types';
import { getReservationsAdditionalUniqueProductTaxValues } from '../../../../modules/productOrders/selectors';
import { hasSelectedEnterpriseNetPricing } from '../../../../selectors/views/selectedEnterprise/selectedEnterprise';
import { ApplicationState } from '../../../../types';

type DelocalizedTaxValue = TaxRate & TaxValue;

interface LocalizedTaxValue extends TaxRate, Omit<TaxValue, 'value'> {
    value: string;
}
interface TaxBreakDown {
    taxTotal: string;
    taxValues: LocalizedTaxValue[];
    hasNetPricing: boolean;
}

const getTotalExtraTaxValue = createSelector(
    getReservationsAdditionalUniqueProductTaxValues,
    flow(
        array.map(({ value }) => value),
        array.reduce(0, sumNumbersAsDecimals)
    )
);

const getTaxTotalValue = createSelector(
    getReservationsTotalPrice,
    getTotalExtraTaxValue,
    getStringProp('currencyCode'),
    (totalPrice, totalExtraTaxValue, currencyCode) => {
        const totalAmount = totalPrice?.[currencyCode];
        if (totalAmount == null) {
            return 0;
        }

        return pipe(
            totalAmount,
            ({ grossValue, netValue }) => subtractNumbersAsDecimals(grossValue, netValue),
            (subTotalTaxes) => sumNumbersAsDecimals(subTotalTaxes, totalExtraTaxValue)
        );
    }
);

const getDelocalizedTaxValues = createSelector(
    getReservationsTotalPrice,
    getStringProp('currencyCode'),
    getReservationsAdditionalUniqueProductTaxValues,
    getTaxRates,
    (totalPrice, currencyCode, excludedPriceFromOfferTaxValues, taxRates) => {
        const totalAmount = totalPrice?.[currencyCode];
        if (totalAmount == null || !taxRates) {
            return [];
        }

        return pipe(
            totalAmount.taxValues,
            array.concat(excludedPriceFromOfferTaxValues),
            array.reduce<TaxValue, DelocalizedTaxValue[]>([], (taxes, { taxRateCode, value }) => {
                const taxRate: TaxRate | undefined = taxRates[taxRateCode];

                if (taxRate == null) {
                    return taxes;
                }

                const taxValue = {
                    ...taxRate,
                    taxRateCode,
                    value,
                };

                return [...taxes, taxValue];
            })
        );
    }
);

const groupByTaxRateCode = flow(
    nonEmptyArray.groupBy((taxRate: DelocalizedTaxValue) => taxRate.code),
    record.toArray,
    array.map(([, taxRates]) => ({
        ...taxRates[0],
        value: pipe(
            taxRates,
            array.map(({ value }) => value),
            array.reduce(0, sumNumbersAsDecimals)
        ),
    }))
);

export const getTaxBreakDown = (
    state: ApplicationState,
    { shouldSelectedCurrencyBeUsed }: { shouldSelectedCurrencyBeUsed: boolean }
): TaxBreakDown => {
    const currencyCode: string = shouldSelectedCurrencyBeUsed
        ? getCurrencyCode(state)
        : getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(state, {});
    const languageCode = getLanguageCode(state);
    const hasNetPricing = hasSelectedEnterpriseNetPricing(state, {});

    const taxValues = pipe(
        getDelocalizedTaxValues(state, { currencyCode }),
        groupByTaxRateCode,
        array.map(({ value, ...taxValue }) => ({
            ...taxValue,
            value: localizeCurrencyValue(state, {
                currencyValue: { value, currencyCode },
                pricingMode: PricingMode.Simple,
                // @ts-expect-error $TSFixMeError: Type 'string | undefined' is not assignable to type 'string'.
                languageCode,
            }),
        }))
    );
    const taxTotalValue = getTaxTotalValue(state, { currencyCode });

    return {
        taxTotal: localizeCurrencyValue(state, {
            currencyValue: { value: taxTotalValue, currencyCode },
            // @ts-expect-error $TSFixMeError: Type 'string | undefined' is not assignable to type 'string'.
            languageCode,
            pricingMode: PricingMode.Simple,
        }),
        taxValues,
        hasNetPricing,
    };
};

const isTaxBreakDownTaxValuesEmpty = flow(getTaxBreakDown, get('taxValues'), isEmpty);

export const shouldDisplayTaxBreakDown = createSelector(
    isTaxBreakDownTaxValuesEmpty,
    (isTaxValuesEmpty) => !isTaxValuesEmpty
);
